import useBrainSearchBot from "@/admin/integrations/chat-bot/util/useBrainSearchBot"
import { BrainSearchInput_ContentUsageFragment$key } from "@/brain-search/internal/__generated__/BrainSearchInput_ContentUsageFragment.graphql"
import SendIcon from "@/core/ui/iconsax/linear/arrow-right.svg"
import StopIcon from "@/core/ui/iconsax/linear/pause.svg"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import styleIf from "@assets/style/util/styleIf"
import {
  DiscoIcon,
  DiscoIconButton,
  DiscoInput,
  DiscoInputProps,
  DiscoText,
} from "@disco-ui"
import DiscoContainerButton from "@disco-ui/button/DiscoContainerButton"
import { lighten, useTheme } from "@material-ui/core"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import { TestIDProps } from "@utils/typeUtils"
import classNames from "classnames"
import { useEffect, useMemo, useRef, useState } from "react"
import { graphql, useFragment } from "react-relay"

interface BrainSearchInputProps
  extends TestIDProps,
    Pick<DiscoInputProps, "disabled" | "multiline" | "placeholder"> {
  onSubmit: (input: string) => void
  onChange?: (input: string) => void
  onAbort?: () => void
  isGenerating?: boolean
  clearOnSubmit?: boolean
  classes?: {
    root?: string
  }
  variant?: "raised" | "flat"
  contentUsageKey?: BrainSearchInput_ContentUsageFragment$key | null
  enableSuggestions?: boolean
  suggestionsPosition?: "above" | "below"
  isSidePanel?: boolean
  autoFocus?: boolean
}

function BrainSearchInput({
  placeholder = "Ask a question...",
  onSubmit,
  onChange,
  onAbort,
  isGenerating = false,
  classes: customClasses,
  multiline = true,
  clearOnSubmit = true,
  variant = "raised",
  contentUsageKey,
  enableSuggestions = false,
  suggestionsPosition = "below",
  isSidePanel = false,
  autoFocus = false,
}: BrainSearchInputProps) {
  const [input, setInput] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const isMobile = useIsMobile()
  const theme = useTheme()
  const brainSearchBot = useBrainSearchBot()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!autoFocus || !inputRef.current) return
    const timer = setTimeout(() => {
      inputRef.current?.focus()
    }, 600) // delay to ensure drawer animation is complete and nothing steals focus

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidePanel, isMobile])

  const hasSuggestions = enableSuggestions && brainSearchBot?.enableSuggestedPrompts
  const suggestions = useMemo(
    () =>
      hasSuggestions ? ArrayUtils.randomItems(brainSearchBot.suggestedPrompts, 3) : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasSuggestions]
  )

  const contentUsage = useFragment<BrainSearchInput_ContentUsageFragment$key>(
    graphql`
      fragment BrainSearchInput_ContentUsageFragment on ContentUsage {
        id
        content {
          name
        }
      }
    `,
    contentUsageKey || null
  )
  const hasSources = !!contentUsage
  const showSuggestions =
    isFocused && suggestions.length > 0 && input.trim().length === 0 && !isSidePanel
  const classes = useStyles({ variant, hasSources, showSuggestions, suggestionsPosition })

  const noPrompt = input.trim().length === 0

  const inputComponent = (
    <DiscoInput
      placeholder={placeholder}
      classes={{
        root: classNames(classes.inputRoot, !enableSuggestions && customClasses?.root),
        input: classes.input,
      }}
      value={input}
      onChange={handleChange}
      onFocus={() => enableSuggestions && setIsFocused(true)}
      onBlur={() => {
        if (enableSuggestions) {
          setIsFocused(false)
        }
      }}
      multiline={multiline}
      inputRef={inputRef}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={!isMobile && autoFocus}
      endAdornment={
        hasSources ? (
          <div className={classes.endAdornment}>
            <div className={classes.sources}>
              <div className={classes.sourceTag}>
                <DiscoIcon icon={"file"} color={theme.palette.groovy.neutral[500]} />
                <DiscoText
                  variant={"body-xs-500"}
                  color={"groovy.neutral.500"}
                  truncateText={1}
                >
                  {contentUsage.content.name}
                </DiscoText>
              </div>
            </div>
            {renderButton()}
          </div>
        ) : (
          renderButton()
        )
      }
      onKeyPress={(e) => {
        if (noPrompt) return

        if (e.key === "Enter" && !e.shiftKey) {
          handleSubmit(e)
        }
      }}
    />
  )

  if (!enableSuggestions) {
    return inputComponent
  }

  return (
    <div className={classNames(classes.rootContainer, customClasses?.root)}>
      <div className={classes.innerContainer}>
        {inputComponent}

        {showSuggestions && (
          <div className={classes.suggestionsContainer}>
            {suggestions.map((suggestion) => (
              <DiscoContainerButton
                key={suggestion}
                className={classes.suggestionItem}
                onMouseDown={(e) => {
                  e.preventDefault() // Prevent the input from losing focus immediately
                  handleSuggestionClick(suggestion)
                }}
              >
                <DiscoText variant={"body-sm"} color={"text.secondary"}>
                  {suggestion}
                </DiscoText>
              </DiscoContainerButton>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  function renderButton() {
    return isGenerating ? (
      <DiscoIconButton
        className={classes.sendButton}
        tooltip={"Stop Generating"}
        onClick={onAbort}
      >
        <StopIcon className={classes.stopIcon} />
      </DiscoIconButton>
    ) : (
      <DiscoIconButton
        className={classes.sendButton}
        disabled={noPrompt}
        haveDisabledStyles
        variant={"primary"}
        onClick={(e) => handleSubmit(e)}
      >
        <SendIcon
          className={classes.sendIcon}
          color={theme.palette.primary.contrastText}
        />
      </DiscoIconButton>
    )
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInput(e.target.value)
    onChange?.(e.target.value)
  }

  function handleSubmit(
    e: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>
  ) {
    onSubmit(input)
    e.preventDefault()
    if (clearOnSubmit) setInput("")
  }

  async function handleSuggestionClick(suggestion: string) {
    await onSubmit(suggestion)
  }
}

type StyleProps = {
  variant: "raised" | "flat"
  hasSources: boolean
  showSuggestions: boolean
  suggestionsPosition: "above" | "below"
}

const useStyles = makeUseStyles((theme) => ({
  rootContainer: {
    position: "relative",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    maxWidth: "720px",
  },
  innerContainer: {
    position: "relative",
    maxWidth: "720px",
    width: "100%",
  },
  inputRoot: ({
    variant,
    hasSources,
    showSuggestions,
    suggestionsPosition,
  }: StyleProps) => ({
    borderRadius: theme.measure.borderRadius.large,
    // override the transparent border from DiscoInput - define complete border for each state
    border: showSuggestions
      ? `1px solid ${theme.palette.primary.main}` // Primary border when suggestions are shown
      : `1px solid ${theme.palette.groovy.neutral[200]}`,
    backgroundColor: theme.palette.background.paper,
    ...styleIf(showSuggestions, {
      ...(suggestionsPosition === "below"
        ? {
            borderBottomLeftRadius: "0 !important",
            borderBottomRightRadius: "0 !important",
          }
        : {
            borderTopLeftRadius: "0 !important",
            borderTopRightRadius: "0 !important",
          }),
    }),
    boxShadow:
      variant === "raised" && !showSuggestions ? theme.palette.groovyDepths.xs : "none",
    width: "100%",
    padding: theme.spacing(1),
    minHeight: "48px",
    maxWidth: "720px",
    position: "relative",
    ...styleIf(variant === "flat", {
      "&:hover:not(.Mui-focused)": {
        backgroundColor: theme.palette.background.paper,
      },
      "&.Mui-focused": {
        borderColor: theme.palette.primary.main,
      },
    }),
    ...styleIf(hasSources, {
      flexDirection: "column",
      gap: theme.spacing(0.5),
    }),
    // Override box shadow for focus state to match suggestions border
    "&.Mui-focused": {
      border: showSuggestions
        ? "none !important"
        : `1px solid ${theme.palette.primary.main} !important`,
      boxShadow: showSuggestions
        ? `0 0 0 1px ${theme.palette.primary.main} !important`
        : variant === "raised"
        ? `${theme.palette.groovyDepths.xs} !important`
        : "none !important",
    },
  }),
  input: {
    padding: theme.spacing(0.75, 1),
    maxHeight: "96px",
    overflowY: "auto",
    flex: "1 1 auto",
    width: `calc(100% - ${theme.spacing(2)}px)`,
  },
  sendButton: {
    borderRadius: theme.measure.borderRadius.medium,
    padding: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    alignSelf: "flex-end",
    height: "32px",
    width: "32px",
    backgroundColor: theme.palette.primary.main,

    "&:hover": {
      backgroundColor: lighten(theme.palette.primary.main, 0.2),
    },
    "& svg": {
      color: `${theme.palette.primary.contrastText} !important`,
    },
  },
  sendIcon: {
    transform: "rotate(-90deg)",
  },
  stopIcon: {
    color: `${theme.palette.common.white} !important`,
  },
  endAdornment: {
    width: "100%",
    display: "flex",
  },
  sources: {
    flexGrow: 1,
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  sourceTag: {
    display: "flex",
    gap: theme.spacing(0.5),
    alignItems: "center",
    borderRadius: theme.measure.borderRadius.medium,
    maxWidth: "100%",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.groovy.neutral[100],
    color: theme.palette.groovy.neutral[500],
    border: `1px solid ${theme.palette.groovy.neutral[200]}`,
    "& svg": {
      width: 16,
      height: 16,
      flexShrink: 0,
    },
  },
  suggestionsContainer: ({ suggestionsPosition }: StyleProps) => ({
    position: "absolute",
    width: "calc(100% + 2px)",
    padding: theme.spacing(1),
    maxWidth: "722px",
    ...(suggestionsPosition === "below"
      ? {
          top: "calc(100% - 1px)",
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.primary.main}`,
          borderBottomLeftRadius: theme.measure.borderRadius.large,
          borderBottomRightRadius: theme.measure.borderRadius.large,
        }
      : {
          bottom: "calc(100% - 1px)",
          borderTop: `1px solid ${theme.palette.primary.main}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderTopLeftRadius: theme.measure.borderRadius.large,
          borderTopRightRadius: theme.measure.borderRadius.large,
        }),
    left: "-1px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "none",
    borderLeft: `1px solid ${theme.palette.primary.main}`,
    borderRight: `1px solid ${theme.palette.primary.main}`,
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
  }),
  suggestionItem: {
    padding: theme.spacing(1),
    borderRadius: theme.measure.borderRadius.medium,
    textAlign: "left",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))

export default BrainSearchInput
